import { useCallback, useEffect, useRef, useState } from "react";

type TextLikeElement = HTMLInputElement | HTMLTextAreaElement;

export const useDebouncedInputChange = <T extends TextLikeElement>({
    value,
    onChange,
    debounceMs,
}: {
    value?: string | null;
    onChange?: React.ChangeEventHandler<T>;
    debounceMs: number;
}) => {
    const isDebounced = debounceMs > 0;
    const normalizedValue = value ?? "";
    const [localValue, setLocalValue] = useState(normalizedValue);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        if (!isDebounced) {
            return;
        }
        setLocalValue(normalizedValue);
    }, [isDebounced, normalizedValue]);

    useEffect(
        () => () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        },
        [],
    );

    const emitChange = useCallback((nextValue: string) => {
        if (!onChangeRef.current) {
            return;
        }
        onChangeRef.current({
            target: { value: nextValue },
            currentTarget: { value: nextValue },
        } as React.ChangeEvent<T>);
    }, []);

    const flushPendingChange = useCallback(
        (nextValue: string) => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = undefined;
            }
            emitChange(nextValue);
        },
        [emitChange],
    );

    const handleChange = useCallback(
        (event: React.ChangeEvent<T>) => {
            const nextValue = event.target.value;
            if (!isDebounced) {
                onChangeRef.current?.(event);
                return;
            }

            setLocalValue(nextValue);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                timerRef.current = undefined;
                emitChange(nextValue);
            }, debounceMs);
        },
        [debounceMs, emitChange, isDebounced],
    );

    const handleBlurFlush = useCallback(
        (event: React.FocusEvent<T>) => {
            if (!isDebounced) {
                return;
            }
            flushPendingChange(event.target.value);
            setLocalValue(event.target.value);
        },
        [flushPendingChange, isDebounced],
    );

    return {
        displayValue: isDebounced ? localValue : normalizedValue,
        handleChange,
        handleBlurFlush,
        isDebounced,
    };
};
